import { GET_THE_APP_PATH } from '~/lib/appStores';
import { businessApplyHref, businessCallHref, businessHero } from './business';
import type { FaqContent, FeatureBlockContent, HeroContent, ImageRef, LinkItem } from './types';

const icon = (name: string) => `/images/payroll/icons/${name}.svg`;

/** "I'm a YC company" — YC founders claim the offer through the Bookface deals page. */
export const payrollYcHref = 'https://bookface.ycombinator.com/deals';
// TODO: point at the payroll promotion application once it exists.
export const payrollOfferHref = businessApplyHref;

export const payrollYcCta: LinkItem = {
  label: 'I’m a YC company',
  href: payrollYcHref,
  external: true,
};
export const payrollOfferCta: LinkItem = {
  label: 'Apply for the offer',
  href: payrollOfferHref,
  external: true,
};

export const payrollTermsNote = 'Terms and Conditions apply';

export const payrollHero: HeroContent = {
  title: 'Pay your team globally with $0 payroll fees.',
  // The mobile Figma frame uses its own headline and a single CTA.
  mobileTitle: 'The USD account for marketing spend',
  // App-download CTA: ButtonLink opens the QR modal on desktop and the store on phones.
  mobileCta: { label: 'Get your nsave card', href: GET_THE_APP_PATH },
  cta: payrollYcCta,
  secondaryCta: payrollOfferCta,
  image: {
    ...businessHero.image,
    // Portrait crop of the same render: the landscape crop leaves the monitor
    // too wide for a phone-height frame.
    mobileSrc: '/images/payroll/hero-mobile.jpg',
  },
};

export const payrollPromo = {
  icon: icon('star'),
  text: 'Move your payroll to nsave and unlock complimentary virtual nsave cards for your team and founders. Offer ends 30 December 2026.',
  learnMoreLabel: 'Learn more',
};

export const payrollOfferModal = {
  title: 'Your global payroll fees? We’ve got them covered.',
  mobileTitle: 'Your payroll fees? We’ve got you covered',
  body: 'Pay your team globally and enjoy free payroll for life, no subscription, per-employee or transfer fees. Plus, we’ll waive the $200 application fee. Run your first payroll within 30 days of opening your account to unlock complimentary digital Visa Platinum cards for your team and Visa Infinite cards for founders.',
  mobileBody:
    'Pay your team globally and enjoy free payroll, no subscription, per-employee or transfer fees. Plus, we’ll waive the $200 application fee. Run your first payroll within 30 days of opening your account to unlock complimentary digital Visa Platinum cards for your team and Visa Infinite cards for founders.',
  stepsTitle: 'How it works',
  steps: [
    { icon: icon('card-123'), label: 'Open your nsave business account.' },
    { icon: icon('bar-chart'), label: 'Complete two qualifying payroll months' },
    { icon: icon('dollar-circle'), label: 'Unlock your complimentary nsave cards' },
  ],
  closingTitle: 'Ready to make payroll free?',
  closingBody:
    'Apply for the payroll promotion to start saving while unlocking exclusive benefits.',
};

export const payrollFree: FeatureBlockContent = {
  title: 'Free Payroll',
  image: {
    src: '/images/payroll/free-payroll.jpg',
    width: 1402,
    height: 1440,
    alt: 'A founder reviewing payroll on a laptop with a coffee at a sunlit desk',
  },
  rows: [
    {
      icon: 'coin',
      logo: icon('dollar'),
      title: 'No setup, no subscription',
      description: 'Start paying your team without a monthly plan or onboarding fee.',
    },
    {
      icon: 'users',
      logo: icon('people'),
      title: 'Grow without extra fees',
      description:
        'From your first hire to your fiftieth, pay your global team without per-employee or payroll transfer fees.',
    },
    {
      icon: 'globe',
      logo: icon('globe'),
      title: 'Let your team choose',
      description:
        'Pay across borders while giving employees the flexibility to receive their salaries in USD or local currency.',
      mobileDescription:
        'Pay across borders while giving employees the option to receive their salaries in USD or local currency.',
    },
  ],
};

export const payrollTeam: FeatureBlockContent = {
  title: 'For Your Team',
  image: {
    src: '/images/payroll/team.jpg',
    width: 1380,
    height: 1440,
    alt: 'An employee holding a phone showing their nsave Visa Platinum card ready to add to Google Wallet',
  },
  rows: [
    {
      icon: 'card',
      logo: '/images/payroll/visa-platinum.png',
      title: 'Visa Platinum',
      description:
        'Eligible employees can receive a complimentary virtual Visa Platinum card when your business meets the payroll requirements.',
      mobileDescription:
        'Every employee gets a free international Visa Platinum card once you run your first payroll.',
    },
    {
      icon: 'phone',
      logo: icon('phone-vibrate'),
      title: 'Add to Wallet',
      description:
        'From business purchases to daily essentials, your team can pay easily with Apple Pay or Google Pay.',
    },
    {
      icon: 'shield',
      logo: icon('shield'),
      title: 'More value in every purchase',
      description:
        'Spend directly in USD with eligible purchase and price protection for added peace of mind.',
      mobileDescription:
        'Keep USD card transactions fee-free, with purchase and price protection for added peace of mind.',
    },
  ],
};

export const payrollFounders: FeatureBlockContent = {
  title: 'For Founders',
  image: {
    src: '/images/payroll/founders.jpg',
    width: 1400,
    height: 1440,
    alt: 'A founder checking their phone in an airport lounge with a suitcase beside them',
  },
  rows: [
    {
      icon: 'card',
      logo: '/images/payroll/visa-infinite.png',
      title: 'Visa Infinite',
      description:
        'Up to 5 eligible founders, directors or major shareholders can receive a complimentary virtual Visa Infinite card.',
      mobileDescription:
        'Unlock a premium Visa Infinite card worth $100, with exclusive privileges designed for life beyond the office.',
    },
    {
      icon: 'send',
      logo: icon('plane'),
      title: 'Travel better, worry less',
      description:
        'Enjoy discounted access to 1,200+ airport lounges across 141 countries, plus travel insurance up to $1.5M for you and your family.',
    },
    {
      icon: 'star',
      logo: icon('star'),
      title: 'More perks wherever you go',
      description:
        'From 24/7 Visa Concierge to luxury hotel upgrades, and complimentary breakfast, enjoy more from every trip.',
    },
  ],
};

export const payrollSavings = {
  // Typical provider pricing the comparison assumes, per employee per month.
  providerTransferFee: 10,
  providerSubscription: 25,
  maxEmployees: 500,
  defaultEmployees: 25,
  presets: [
    { label: 'Small team', employees: 25 },
    { label: 'Growing team', employees: 100 },
    { label: 'Large team', employees: 250 },
  ],
  teamSizeLabel: 'Team size',
  providerTitle: 'Typical provider',
  nsaveTitle: 'nsave',
  columns: { employees: 'Employees', months: 'Month', fee: 'Fee', total: 'Total' },
  rows: { transferFee: 'Transfer fee', subscription: 'Payroll subscription' },
  rowCaption: (employees: number, months: number) => `${employees} employees x ${months} months`,
  savingsNote: (yearlySavings: string, employees: number) =>
    `With nsave, you don't have to worry about subscription fees, per-employee or payroll transfer fees. Unlike traditional providers, nsave lets you pay your team globally at no extra cost, helping your business save up to ${yearlySavings} a year for a team of ${employees}.`,
  primaryCta: { label: 'Apply for payroll', href: businessApplyHref, external: true } as LinkItem,
  secondaryCta: { label: 'Book a demo', href: businessCallHref } as LinkItem,
};

export const payrollClosing: {
  title: string;
  image: ImageRef;
  steps: { title: string; body: string }[];
} = {
  title: 'Ready to make payroll free?',
  image: {
    src: '/images/payroll/cta.jpg',
    mobileSrc: '/images/payroll/cta-mobile.jpg',
    width: 2800,
    height: 1466,
    alt: 'A founder walking teammates through payroll documents around a meeting table',
  },
  steps: [
    {
      title: 'Claim your offer',
      body: 'Apply for the payroll promotion, or use your existing nsave Business account',
    },
    {
      title: 'Run your first payroll',
      body: 'Pay your team globally through nsave Business Account and meet the qualifying payroll requirements.',
    },
    {
      title: 'Unlock your nsave cards',
      body: 'Get complimentary virtual Visa Platinum cards for eligible employees and Visa Infinite cards for up to 5 eligible founders.',
    },
  ],
};

export const payrollFaq: FaqContent = {
  title: 'Frequently asked Questions',
  items: [
    {
      question: 'Who is eligible for the payroll promotion?',
      answer:
        'Any company that opens an nsave business account and runs eligible payroll through nsave before Dec 31 is eligible.',
    },
    {
      question: 'Is payroll really free for life?',
      answer:
        'Yes, eligible companies pay no payroll fees for as long as they run their first eligible payroll.',
    },
    {
      question: 'Can I pay employees in different currencies?',
      answer:
        'Yes, you can pay your team in USD, EUR, GBP and local supported currencies from one account, wherever they’re based.',
    },
    {
      // TODO: draft answer, not yet approved. Mobile-only question from the mobile Figma.
      question: 'How long does it take to open an account?',
      answer:
        'Applying takes a few minutes online. Once your business passes our verification checks, your nsave Business account is ready to fund and run payroll.',
      mobileOnly: true,
    },
    {
      question: 'How do I qualify for the complimentary nsave cards?',
      answer:
        'Run your first payroll through nsave and your team members / founders will automatically get a free nsave card.',
    },
    {
      question: 'What’s the difference between Visa Platinum and Visa Infinite?',
      answer:
        'Both cards work worldwide with no annual fee. Visa Infinite is the premium tier, with discounted access to airport lounge access and travel benefits.',
    },
    {
      question: 'Do I need to be a YC company to apply?',
      answer:
        'No, any registered business can apply, but YC companies will get the $200 application fee waived. Non-YC companies can get the fee refunded if they meet the payroll criteria.',
    },
  ],
};

export const payrollFaqFootnote =
  'Please note: the nsave Card is a distinct, standalone product and operates separately from nsave’s other accounts and services. The card can be used everywhere Visa is accepted';
